import { useCallback, useEffect, useRef, useState } from "react";
import { useAudioCapture } from "./useAudioCapture";
import { encodeAudioForAPI } from "@/utils/audioProcessor";
import { AudioQueue } from "@/utils/audioQueue";
import { createOnceAsync } from "@/utils/onceAsync";
import { TARGET_SAMPLE_RATE } from "@/utils/pcm";
import { parseVoiceEvent } from "@/utils/voiceEvents";
import { persistInterpreterSession, type PersistInterpreterResult } from "@/utils/persistInterpreterSession";
import { supabase } from "@/integrations/supabase/client";
import { buildRealtimeVoiceWsUrl } from "@/integrations/supabase/project";
import {
  INTERPRETER_VOICE_CONTEXT,
  applyInterpreterDelta,
  canCaptureWhileHolding,
  matchSqueezeToScene,
  nextInterpreterTalk,
  type InterpreterSpeaker,
  type InterpreterTurn,
} from "@/lib/interpreter";

export type InterpreterStatus = "idle" | "connecting" | "ready" | "holding" | "speaking";

const EMPTY_PERSIST = (transcripts: InterpreterTurn[]): PersistInterpreterResult => {
  const matched = matchSqueezeToScene(
    transcripts.map((item) => item.text).join(" "),
  );
  return {
    sessionId: null,
    situationId: null,
    matchedSlug: matched.slug,
    matchedTitle: matched.title,
  };
};

export function useInterpreterVoice() {
  const [status, setStatus] = useState<InterpreterStatus>("idle");
  const [transcripts, setTranscripts] = useState<InterpreterTurn[]>([]);
  const [holding, setHolding] = useState<InterpreterSpeaker | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ended, setEnded] = useState<PersistInterpreterResult | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const audioQueueRef = useRef<AudioQueue | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const transcriptsRef = useRef<InterpreterTurn[]>([]);
  const holdingRef = useRef<InterpreterSpeaker | null>(null);
  const readyRef = useRef(false);
  const sessionStartedAtRef = useRef<number | null>(null);
  const persistOnceRef = useRef(createOnceAsync<PersistInterpreterResult>());
  const closingRef = useRef(false);
  const captureStartedRef = useRef(false);
  const { startCapture, stopCapture } = useAudioCapture();

  useEffect(() => {
    transcriptsRef.current = transcripts;
  }, [transcripts]);

  const persistIfNeeded = useCallback(async (): Promise<PersistInterpreterResult> => {
    const startedAt = sessionStartedAtRef.current;
    if (!startedAt) return EMPTY_PERSIST(transcriptsRef.current);
    return persistOnceRef.current.run(async () => {
      try {
        return await persistInterpreterSession({
          startedAt,
          transcripts: transcriptsRef.current,
        });
      } catch (persistError) {
        console.error("Error persisting interpreter session:", persistError);
        return EMPTY_PERSIST(transcriptsRef.current);
      }
    });
  }, []);

  const stopMic = useCallback(() => {
    captureStartedRef.current = false;
    try {
      stopCapture();
    } catch {
      // teardown
    }
  }, [stopCapture]);

  const teardown = useCallback(() => {
    closingRef.current = true;
    readyRef.current = false;
    holdingRef.current = null;
    setHolding(null);
    stopMic();
    const ws = wsRef.current;
    wsRef.current = null;
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
      try {
        ws.close();
      } catch {
        // ignore
      }
    }
    try {
      audioQueueRef.current?.clear();
    } catch {
      // ignore
    }
    setStatus("idle");
  }, [stopMic]);

  const sendJson = useCallback((payload: unknown) => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(payload));
    }
  }, []);

  const startMicIfHolding = useCallback(async () => {
    if (!canCaptureWhileHolding(readyRef.current, holdingRef.current)) return;
    if (captureStartedRef.current) return;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    captureStartedRef.current = true;
    setStatus("holding");
    try {
      await startCapture((audioData) => {
        if (!canCaptureWhileHolding(readyRef.current, holdingRef.current)) return;
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({
            type: "session.input_audio.append",
            audio: encodeAudioForAPI(audioData),
          }));
        }
      }, audioContextRef.current ?? undefined);
    } catch (captureError) {
      captureStartedRef.current = false;
      const denied = captureError instanceof DOMException && captureError.name === "NotAllowedError";
      setError(denied
        ? "Permita o microfone no navegador para traduzir."
        : captureError instanceof Error ? captureError.message : "Não foi possível capturar o áudio");
    }
  }, [startCapture]);

  const connect = useCallback(async () => {
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
      return;
    }

    setError(null);
    setEnded(null);
    setStatus("connecting");
    setTranscripts([]);
    transcriptsRef.current = [];
    persistOnceRef.current.reset();
    sessionStartedAtRef.current = null;
    readyRef.current = false;
    closingRef.current = false;

    if (!audioContextRef.current || audioContextRef.current.state === "closed") {
      audioContextRef.current = new AudioContext({ sampleRate: TARGET_SAMPLE_RATE });
      audioQueueRef.current = null;
    }
    if (audioContextRef.current.state === "suspended") {
      await audioContextRef.current.resume();
    }
    if (!audioQueueRef.current) {
      audioQueueRef.current = new AudioQueue(audioContextRef.current);
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error("Sessão expirada. Entre novamente.");
    }

    const wsUrl = buildRealtimeVoiceWsUrl(session.access_token, {
      url: import.meta.env.VITE_SUPABASE_URL,
      publishableKey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    }, { mode: "interpreter" });
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    let opened = false;

    ws.onopen = () => {
      opened = true;
      ws.send(JSON.stringify({
        type: "hellopeople.context",
        context: INTERPRETER_VOICE_CONTEXT,
      }));
    };

    ws.onmessage = async (event) => {
      try {
        const parsed = parseVoiceEvent(JSON.parse(String(event.data)));
        switch (parsed.kind) {
          case "session_ready":
            readyRef.current = true;
            sessionStartedAtRef.current = Date.now();
            persistOnceRef.current.reset();
            setStatus(holdingRef.current ? "holding" : "ready");
            await startMicIfHolding();
            break;
          case "audio_delta":
            setStatus("speaking");
            await audioQueueRef.current?.addToQueue(parsed.pcm);
            break;
          case "user_transcript":
            if (holdingRef.current) {
              setTranscripts((prev) => applyInterpreterDelta(prev, "user", holdingRef.current!, parsed.text));
            }
            break;
          case "assistant_delta": {
            const speaker = holdingRef.current
              ?? transcriptsRef.current[transcriptsRef.current.length - 1]?.speaker
              ?? "you";
            setTranscripts((prev) => applyInterpreterDelta(prev, "assistant", speaker, parsed.text));
            break;
          }
          case "error":
            setError(
              parsed.reason === "payment_required" || parsed.reason === "limit_reached"
                ? "Assine para usar a ajuda ao vivo."
                : parsed.error || "Erro ao conectar com o servidor",
            );
            break;
          default:
            break;
        }
      } catch {
        // frame inválido
      }
    };

    ws.onerror = () => {
      if (opened && !closingRef.current) {
        setError("Não foi possível estabelecer conexão");
      }
    };

    ws.onclose = () => {
      readyRef.current = false;
      stopMic();
      if (!opened && !closingRef.current) {
        setError("Sessão inválida ou assinatura necessária para a ajuda ao vivo.");
      }
      if (!closingRef.current) {
        setStatus("idle");
      }
    };
  }, [startMicIfHolding, stopMic]);

  const stopTalk = useCallback(() => {
    const was = holdingRef.current;
    holdingRef.current = null;
    setHolding(null);
    stopMic();
    if (was) {
      sendJson({ type: "hellopeople.commit" });
    }
    if (readyRef.current) {
      setStatus("ready");
    }
  }, [sendJson, stopMic]);

  const startTalk = useCallback(async (speaker: InterpreterSpeaker) => {
    setError(null);
    holdingRef.current = speaker;
    setHolding(speaker);
    try {
      if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
        await connect();
      }
      sendJson({ type: "hellopeople.speaker", speaker });
      await startMicIfHolding();
    } catch (connectError) {
      setError(connectError instanceof Error ? connectError.message : "Erro ao conectar");
      setStatus("idle");
    }
  }, [connect, sendJson, startMicIfHolding]);

  const toggle = useCallback(async (speaker: InterpreterSpeaker) => {
    const next = nextInterpreterTalk(holdingRef.current, speaker);
    if (next.action === "stop") {
      stopTalk();
      return;
    }
    if (next.action === "switch") {
      stopTalk();
    }
    await startTalk(speaker);
  }, [startTalk, stopTalk]);

  const endSession = useCallback(async (): Promise<PersistInterpreterResult> => {
    holdingRef.current = null;
    setHolding(null);
    stopMic();
    let result = EMPTY_PERSIST(transcriptsRef.current);
    try {
      result = await persistIfNeeded();
    } catch {
      result = EMPTY_PERSIST(transcriptsRef.current);
    }
    try {
      teardown();
    } catch {
      setStatus("idle");
    }
    setEnded(result);
    return result;
  }, [persistIfNeeded, stopMic, teardown]);

  useEffect(() => {
    return () => {
      try {
        teardown();
      } catch {
        // unmount
      }
      try {
        audioContextRef.current?.close();
      } catch {
        // ignore
      }
    };
  }, [teardown]);

  return {
    status,
    transcripts,
    holding,
    error,
    ended,
    toggle,
    endSession,
  };
}
